import { useEffect, useRef, useState } from "react";
import { auth } from "../firebase";

/**
 * Botón de PayPal unificado. Carga el SDK, renderiza los botones,
 * y delega create + capture al backend enviando el token de Firebase.
 *
 * Props:
 *   clientId        — PayPal client ID
 *   createEndpoint  — ruta backend para crear la orden  (ej. "/promo/create-order")
 *   captureEndpoint — ruta backend para capturar la orden (ej. "/promo/capture-order")
 *   createBody      — objeto plano que se envía en el create (sin orderID)
 *   captureBody     — objeto plano que se envía en el capture (sin orderID, se agrega automáticamente)
 *   onSuccess(result) — callback al completar el pago
 *   onError({ message }) — callback en caso de error
 *   dedupeOrders    — evita doble captura del mismo orderID (default false)
 */
export default function PayPalButtonBase({
  clientId,
  createEndpoint,
  captureEndpoint,
  createBody,
  captureBody,
  onSuccess,
  onError,
  dedupeOrders = false,
}) {
  const paypalRef        = useRef(null);
  const hasRendered      = useRef(false);
  const processedOrders  = useRef(new Set());
  const [loading, setLoading]     = useState(true);
  const [sdkError, setSdkError]   = useState(null);

  // Refs para leer siempre los valores actuales dentro del efecto
  // sin necesidad de relanzarlo cuando cambian (los padres usan key para remount)
  const createBodyRef      = useRef(createBody);
  const captureBodyRef     = useRef(captureBody);
  const onSuccessRef       = useRef(onSuccess);
  const onErrorRef         = useRef(onError);
  createBodyRef.current    = createBody;
  captureBodyRef.current   = captureBody;
  onSuccessRef.current     = onSuccess;
  onErrorRef.current       = onError;

  const baseURL = process.env.REACT_APP_BACKEND_URL;

  // ── Carga del SDK ─────────────────────────────────────────────────────────
  useEffect(() => {
    let isCancelled = false;
    const scriptId = "paypal-sdk";

    const loadScript = () =>
      new Promise((resolve, reject) => {
        const existing = document.getElementById(scriptId);
        if (existing) {
          if (window.paypal) resolve();
          else {
            existing.addEventListener("load", resolve);
            existing.addEventListener("error", () =>
              reject(new Error("Error cargando PayPal SDK"))
            );
          }
          return;
        }
        const script = document.createElement("script");
        script.id    = scriptId;
        script.src   = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
        script.async = true;
        script.onload  = resolve;
        script.onerror = () => reject(new Error("Error cargando PayPal SDK"));
        document.body.appendChild(script);
      });

    const attemptLoad = async (retries = 3) => {
      try {
        await loadScript();
        if (!isCancelled) setLoading(false);
      } catch (err) {
        console.error(`PayPal SDK load error (attempt ${4 - retries}/3):`, err);
        if (retries > 1) {
          setTimeout(() => attemptLoad(retries - 1), 1000);
        } else {
          setSdkError("No se pudo cargar PayPal. Recarga la página.");
          setLoading(false);
          if (!isCancelled) onErrorRef.current({ message: "No se pudo cargar el SDK de PayPal." });
        }
      }
    };

    attemptLoad();
    return () => { isCancelled = true; };
  }, [clientId]); // solo recarga si cambia el clientId

  // ── Renderizado de botones ─────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return;
    let isCancelled = false;

    const renderButtons = async () => {
      if (!paypalRef.current || !window.paypal || isCancelled) return;
      if (hasRendered.current) return;
      hasRendered.current = true;
      paypalRef.current.innerHTML = "";

      try {
        window.paypal
          .Buttons({
            createOrder: async () => {
              const idToken = await auth.currentUser.getIdToken();
              const res = await fetch(`${baseURL}${createEndpoint}`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${idToken}`,
                },
                body: JSON.stringify(createBodyRef.current),
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || "Error al crear la orden");
              return data.orderId || data.orderID;
            },

            onApprove: async (data) => {
              if (dedupeOrders) {
                if (processedOrders.current.has(data.orderID)) return;
                processedOrders.current.add(data.orderID);
              }
              try {
                const idToken = await auth.currentUser.getIdToken();
                const res = await fetch(`${baseURL}${captureEndpoint}`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${idToken}`,
                  },
                  body: JSON.stringify({ orderID: data.orderID, ...captureBodyRef.current }),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result.error || "Error al capturar el pago");
                if (!isCancelled) onSuccessRef.current({ orderId: data.orderID, ...result });
              } catch (err) {
                console.error("Error capturing payment:", err);
                if (!isCancelled) onErrorRef.current({ message: err.message });
              }
            },

            onError: (err) => {
              console.error("PayPal SDK error:", err);
              if (!isCancelled) onErrorRef.current({ message: err.message || "Error en PayPal" });
            },
          })
          .render(paypalRef.current)
          .catch((err) => {
            console.error("Error rendering PayPal buttons:", err);
            setSdkError(err.message || "Error al mostrar el botón de PayPal.");
          });
      } catch (err) {
        console.error("Error in renderButtons:", err);
        if (!isCancelled) onErrorRef.current({ message: err.message || "Error al renderizar PayPal" });
      }
    };

    renderButtons();

    return () => {
      isCancelled = true;
      hasRendered.current = false;
      if (dedupeOrders) processedOrders.current.clear();
      if (paypalRef.current) paypalRef.current.innerHTML = "";
    };
  }, [loading, baseURL, createEndpoint, captureEndpoint, dedupeOrders]);

  return (
    <div className="w-full flex flex-col items-center">
      {loading   && <div className="text-gray-600">Cargando PayPal...</div>}
      {sdkError  && <div className="text-red-500 font-semibold">{sdkError}</div>}
      <div ref={paypalRef} className="w-full flex justify-center" />
    </div>
  );
}
